import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { compact, get } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import {
  type Client,
  type CreateClientRewriteMutationVariables,
  CustomTaxonomyEnum,
  MetadataKey,
  Role,
  S3Format,
} from '@revelio/data-access';

import { CreateClientMutation } from './admin-user-edit/admin-user-client-group-select';
import ClientForm, {
  type ClientFormValues,
  type ReportSelectOption,
} from './client-form';
import { serializeRequestsPerSecond } from './requests-per-second-modal';
import UserForm, { type UserFormValues } from './user-form';
import { ClientDetailsQuery } from './userOperations';
import {
  getReportOptions,
  normalizeAdditionalPermissions,
  serializeOptions,
  USER_ROLE_OPTION,
  useAllClientUsersQuery,
  useSubmitUser,
} from './utils/helpers';

interface AdminClientAddProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminClientAdd = ({ isOpen, onClose }: AdminClientAddProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const toast = useToast();

  const {
    control: clientControl,
    register,
    formState: { errors },
    handleSubmit,
    watch: clientWatch,
  } = useForm<ClientFormValues>();

  const userFormMethods = useForm<UserFormValues>({
    defaultValues: { role: USER_ROLE_OPTION },
  });
  const { loggedInUser } = useGetLoggedInUser();
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;

  const [, createClientGroup] = useMutation(CreateClientMutation);

  const hasDeliverablesTab = clientWatch('tabs', [])?.some(
    (tab) => tab?.value === 'deliverables'
  );

  const hasReportsTab = clientWatch('tabs', [])?.some(
    (tab) => tab?.value === 'reports'
  );

  const [{ fetching: fetchingClient, data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [loggedInUser?.client_name as string] },
    pause: !loggedInUser?.client_name,
  });

  const [reportOptions, setReportOptions] = useState<ReportSelectOption[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const client = get(clientData, 'clients[0]');

    if (!fetchingClient && client) {
      const allReportTypes = getReportOptions(client);

      setReportOptions(allReportTypes);
    }
  }, [clientData, hasReportsTab, fetchingClient]);

  const { addUser } = useSubmitUser({
    loggedInUser,
    isClientAdmin,
    onClose,
  });

  const [clientName, setClientName] = useState<string>('');
  const { refetch } = useAllClientUsersQuery(clientName);

  const onSubmit = handleSubmit((formData) => {
    // Defaulting to selectedClient's data_builder_configuration if formData.pipeline_types is undefined
    const pipelineTypesToUse = formData.pipeline_types
      ? serializeOptions(formData.pipeline_types).pipelineTypes
      : [];

    const reportTypesToUse = formData.report_types
      ? serializeOptions(formData.report_types).reportTypes
      : [];

    const postingSourcesToUse = formData.pipeline_types
      ? serializeOptions(formData.pipeline_types).postingSources
      : [];

    const s3Location = formData.s3_location
      ? {
          s3_bucket: formData.s3_location,
          s3_file_prefix: '', // placeholders
          s3_format: S3Format.Csv, // placeholders
        }
      : undefined;

    const snowflakeLocation =
      formData.snowflake_database || formData.snowflake_schema
        ? {
            snowflake_db: formData.snowflake_database || '',
            snowflake_schema: formData.snowflake_schema || '',
            snowflake_file_prefix: '', // placeholder
          }
        : undefined;

    const additionalPermissionsToUse = normalizeAdditionalPermissions(
      formData.additional_permissions
    );
    const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser.role);
    const apiMetadata = [
      {
        key: MetadataKey.RequestsPerSecond,
        value: serializeRequestsPerSecond(formData.requests_per_second),
      },
      {
        key: MetadataKey.RequestsPerMonth,
        value: formData.requests_per_month,
      },
    ].filter(
      (metadataValue) =>
        metadataValue.value !== undefined &&
        metadataValue.value !== null &&
        metadataValue.value !== ''
    );

    const formatData: CreateClientRewriteMutationVariables = {
      client_name: formData.client_name,
      tabs: compact(formData.tabs.map((tab) => tab.value)),
      live: formData.live,
      active: true, // backend cannot create inactive client group. Can only be edited
      linkup_postings: formData.linkup_postings,
      num_seats: formData?.num_seats?.toString(),
      custom_taxonomy:
        formData.custom_taxonomy?.value || CustomTaxonomyEnum.Adaptive,
      data_builder_configuration: (() => {
        const config: CreateClientRewriteMutationVariables['data_builder_configuration'] =
          {};

        if (hasDeliverablesTab) {
          config.pipeline_type = pipelineTypesToUse;
          config.posting_source = postingSourcesToUse;
          config.s3_location = s3Location;
          config.snowflake_location = snowflakeLocation;
        }

        if (additionalPermissionsToUse) {
          config.additional_permissions = additionalPermissionsToUse;
        }

        return config;
      })(),
      ...(isRevelioAdmin && apiMetadata.length > 0
        ? { metadata: apiMetadata }
        : {}),
      ...(hasReportsTab && reportTypesToUse
        ? { reports_configuration: { report_types: reportTypesToUse } }
        : {}),
    };

    return createClientGroup(formatData).then((result) => {
      if (result?.data?.createClientGroup && !result.error) {
        if (searchParams.get('editing') !== 'user') {
          const newClientName = result.data?.createClientGroup?.client_name;
          const {
            company_lists: _company_lists,
            tabs: _tabs,
            ...updatedFormatData
          } = formatData;
          const newClient = {
            ...updatedFormatData,
            ...(newClientName ? { client_name: newClientName } : {}),
          };
          const newSearchParams = new URLSearchParams(searchParams);

          userFormMethods.handleSubmit(async (formData) => {
            if (newClientName) {
              await addUser({
                formData,
                specifiedClient: newClient as unknown as Client,
              });

              setClientName(newClientName);
              refetch();
            }
          })();

          newSearchParams.set('client', newClientName ?? '');

          setSearchParams(newSearchParams);
        }
        onClose();
      } else {
        toast({
          title: 'Error',
          description: 'An error occurred',
          status: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent minWidth="65%" maxHeight="95vh" margin="5">
        <ModalHeader>Add a new client</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <FormControl id="name" isInvalid={!!errors.client_name}>
            <FormLabel fontSize="sm" fontWeight="semibold">
              Client Name
            </FormLabel>
            <Input
              {...register('client_name', {
                required: 'Name is required.',
                minLength: {
                  value: 1,
                  message: 'Minimum length should be 1',
                },
              })}
            />
            <FormErrorMessage>
              {errors.client_name && errors.client_name.message}
            </FormErrorMessage>
          </FormControl>
          <ClientForm
            clientToEdit={null}
            control={clientControl}
            register={register}
            watch={clientWatch}
            errors={errors}
            reportOptions={hasReportsTab ? reportOptions : []}
          />
          <Text
            fontSize={20}
            fontWeight={600}
            color="blackAlpha.900"
            mt={6}
            mb={4}
          >
            Add a new user
          </Text>
          <UserForm
            useFormMethods={userFormMethods}
            userToEdit={null}
            setPassword={(newPassword: string) =>
              userFormMethods.setValue('password', newPassword)
            }
            hideChangeClientGroup
          />
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button onClick={onSubmit} colorScheme="green">
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminClientAdd;
